import { useState } from "react";
import { toast } from "sonner";
import { api, formatError, useApi } from "@/lib/api";
import { useAuth } from "./AuthContext";
import { AdminTitle, AInput, ATextarea, ASelect, StatusPill, Table, commaToList, linesToList } from "./AdminUi";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { slug: "", name: "", description: "", audiences: "", support_types: "", coverage: "UK-wide", website: "", contacts: "", availability: "", verification: "needs_review", last_checked: "", featured: false, urgent: false, status: "published", admin_note: "" };

const toForm = (e) => ({ ...EMPTY, ...e, audiences: e.audiences.join(", "), support_types: e.support_types.join(", "), contacts: e.contacts.map((c) => `${c.label} | ${c.value} | ${c.href}`).join("\n"), last_checked: e.last_checked || "" });

const parseContacts = (s) => linesToList(s).map((l) => {
  const [label = "", value = "", href = ""] = l.split("|").map((x) => x.trim());
  return { label, value, href };
});

export default function AdminDirectory() {
  const { user } = useAuth();
  const { data, reload } = useApi("/admin/directory");
  const [f, setF] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...f, audiences: commaToList(f.audiences), support_types: commaToList(f.support_types), contacts: parseContacts(f.contacts), last_checked: f.last_checked || null };
    delete payload.id; delete payload.order;
    try {
      if (f.id) await api.put(`/admin/directory/${f.id}`, payload);
      else await api.post("/admin/directory", payload);
      toast.success("Directory entry saved");
      setF(null);
      reload();
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try { await api.delete(`/admin/directory/${d.id}`); toast.success("Entry deleted"); reload(); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div data-testid="admin-directory">
      <AdminTitle title="Support directory" actions={<button className="btn-primary" onClick={() => setF({ ...EMPTY })} data-testid="admin-new-directory-btn">New entry</button>}>Only mark an entry as verified after checking it against the organisation's own website. Unverified contact details should stay in the internal note.</AdminTitle>
      <Table head={["Organisation", "Verification", "Last checked", "Status", "Actions"]} testId="admin-directory-table">
        {(data || []).map((d) => (
          <tr key={d.id} data-testid={`admin-directory-row-${d.slug}`}>
            <td className="px-4 py-3 font-medium">{d.name}{d.admin_note && <p className="text-xs text-[#8A5300] font-normal mt-1">{d.admin_note}</p>}</td>
            <td className="px-4 py-3"><StatusPill status={d.verification} /></td>
            <td className="px-4 py-3 whitespace-nowrap">{d.last_checked || "Never"}</td>
            <td className="px-4 py-3"><StatusPill status={d.status} /></td>
            <td className="px-4 py-3 space-x-3 whitespace-nowrap">
              <button className="btn-ghost" onClick={() => setF(toForm(d))} data-testid={`admin-edit-directory-${d.slug}`}>Edit</button>
              {user.role === "admin" && <button className="btn-ghost !text-clay-dark" onClick={() => remove(d)} data-testid={`admin-delete-directory-${d.slug}`}>Delete</button>}
            </td>
          </tr>
        ))}
      </Table>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-cream">
          <DialogTitle className="font-serif text-2xl">{f?.id ? "Edit entry" : "New entry"}</DialogTitle>
          {f && (
            <form onSubmit={save} className="space-y-4" data-testid="directory-editor-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <AInput id="dir-name" label="Organisation name" value={f.name} onChange={set("name")} required />
                <AInput id="dir-slug" label="Identifier (slug)" value={f.slug} onChange={set("slug")} required pattern="[a-z0-9-]+" />
              </div>
              <ATextarea id="dir-description" label="Description" value={f.description} onChange={set("description")} rows={3} />
              <div className="grid gap-4 sm:grid-cols-2">
                <AInput id="dir-audiences" label="Audiences (comma separated)" hint="anyone, families, parents, partners, friends, children-young-people" value={f.audiences} onChange={set("audiences")} />
                <AInput id="dir-types" label="Support types (comma separated)" hint="emergency, helpline, text-chat, support-groups, information, local-services" value={f.support_types} onChange={set("support_types")} />
                <AInput id="dir-coverage" label="Geographic coverage" value={f.coverage} onChange={set("coverage")} />
                <AInput id="dir-website" label="Website" type="url" value={f.website} onChange={set("website")} />
              </div>
              <ATextarea id="dir-contacts" label="Contacts (one per line: Label | Value | link)" hint="e.g. Helpline | 0800 000 000 | tel:0800000000" value={f.contacts} onChange={set("contacts")} rows={3} />
              <AInput id="dir-availability" label="Availability" value={f.availability} onChange={set("availability")} />
              <div className="grid gap-4 sm:grid-cols-3">
                <ASelect id="dir-verification" label="Verification" value={f.verification} onChange={set("verification")} options={[["needs_review", "Needs review"], ["verified", "Verified"]]} />
                <AInput id="dir-checked" label="Date last checked" type="date" value={f.last_checked} onChange={set("last_checked")} />
                <ASelect id="dir-status" label="Visibility" value={f.status} onChange={set("status")} options={[["published", "Published"], ["draft", "Hidden"]]} />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured")(e.target.checked)} className="h-5 w-5 accent-[#2F6B6A]" data-testid="dir-featured-checkbox" />Featured on homepage</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={f.urgent} onChange={(e) => set("urgent")(e.target.checked)} className="h-5 w-5 accent-[#2F6B6A]" data-testid="dir-urgent-checkbox" />Show on Urgent Help</label>
              </div>
              <ATextarea id="dir-note" label="Internal note (never shown publicly)" value={f.admin_note} onChange={set("admin_note")} rows={2} />
              <button type="submit" className="btn-primary" data-testid="directory-save-button">Save entry</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
